// src/context/ThemeContext.tsx
// ════════════════════════════════════════════════════════════
//  THEME CONTEXT
//  Wraps the entire app. Provides { theme, isDark, toggleTheme }
//  to any component without prop drilling.
//
//  Usage:
//    import { useThemeContext } from '../context/ThemeContext';
//    const { isDark, toggleTheme } = useThemeContext();
// ════════════════════════════════════════════════════════════

import React, { createContext, useContext, ReactNode } from 'react';
import { useTheme, Theme } from '../hooks/useTheme';

interface ThemeContextValue {
  theme:       Theme;
  isDark:      boolean;
  isLight:     boolean;
  toggleTheme: () => void;
  setDark:     () => void;
  setLight:    () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const themeState = useTheme();
  return (
    <ThemeContext.Provider value={themeState}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useThemeContext(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeContext must be used within <ThemeProvider>');
  return ctx;
}

export default ThemeProvider;
